import React from 'react';
import { SerializedStyles, css, keyframes } from '@emotion/react';
import { ProgressIndicatorInterface } from './types';
import { useTheme } from '@veneer/theme';
import getPillBorder from '../utils/get_pill_border';

type StylesType = {
  root: {
    base: SerializedStyles;
    circular: SerializedStyles;
    linear: {
      base: SerializedStyles;
      withLabel: {
        base: SerializedStyles;
        center: SerializedStyles;
        trailing: SerializedStyles;
      };
    };
    hc: SerializedStyles;
  };
  progressBar: Record<'circular' | 'linear', SerializedStyles>;
  circular: Record<'base' | 'indeterminate', SerializedStyles>;
  linear: Record<
    'base' | 'indeterminate' | 'determinate' | 'bar' | 'barHc' | 'sharp',
    SerializedStyles
  >;
  label: Record<'base' | 'floating' | 'circular', SerializedStyles>;
  circle: Record<'base' | 'determinate', SerializedStyles>;
};

const linearIndeterminateAnimation = keyframes({
  '0%': { width: '0%', left: 0, right: 0 },
  '50%': { width: '100%', left: 0, right: 0 },
  '100%': { width: '0%', left: '100%', right: 0 },
});

const circularIndeterminateAnimation = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
});

const useStyles = ({
  size,
  color,
  value = 0,
  trackThickness,
}: {
  size: number;
  color: ProgressIndicatorInterface['color'];
  value: number;
  trackThickness: ProgressIndicatorInterface['trackThickness'];
}): StylesType => {
  const theme = useTheme();

  const roundedValue = Math.round(value);
  const floatingLabelOffset =
    value === 0
      ? '+ 4px'
      : value === 1
        ? '+ 2px'
        : value >= 98
          ? '- 44px'
          : '- 16px';

  const radius = 11;
  const circumference = 2 * Math.PI * radius;
  const progress = value / 100;
  const dashoffset = circumference * (1 - progress);

  return React.useMemo(
    () => ({
      root: {
        base: css({
          display: 'block',
          color: color ?? theme.color.primary.base.default,
        }),
        circular: css({
          position: 'relative',
          display: 'inline-block',
        }),
        linear: {
          base: css({
            width: '100%',
          }),
          withLabel: {
            base: css({
              display: 'flex',
              flexDirection: 'column',
              gap: theme.layout.size1,
              alignItems: 'flex-start',
            }),
            center: css({
              alignItems: 'center',
            }),
            trailing: css({
              alignItems: 'flex-end',
            }),
          },
        },
        hc: css({
          color: theme.color.foreground.default,
        }),
      },

      progressBar: {
        circular: css({
          display: 'block',
          height: size,
          width: size,
          overflow: 'hidden',
        }),

        linear: css({
          // Default border should look like a pill
          borderRadius: getPillBorder,
          // if trackThickness is not provided, use the default size1 value
          height: trackThickness || size,
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
        }),
      },

      circular: {
        base: css({
          stroke: 'currentColor',
          transform: 'rotate(-90deg)',
          '> circle': {
            transition: 'stroke-dashoffset 0.2s linear',
          },
        }),

        indeterminate: css({
          animationDuration: '750ms',
          animationIterationCount: 'infinite',
          animationName: circularIndeterminateAnimation,
          animationTimingFunction: 'linear',
        }),
      },

      linear: {
        base: css({
          backgroundColor: 'currentColor',
          borderRadius: getPillBorder,
          bottom: 0,
          left: 0,
          position: 'absolute',
          top: 0,
          transformOrigin: 'left',
          transition: 'transform 0.2s linear',
          width: '100%',
        }),

        indeterminate: css({
          animationDuration: '2s',
          animationIterationCount: 'infinite',
          animationName: linearIndeterminateAnimation,
          width: 'auto',
        }),

        determinate: css({
          transform: `translateX(${value - 100}%)`,
        }),

        bar: css({
          backgroundColor: theme.color.background.disabled,
          borderRadius: getPillBorder,
          bottom: 0,
          left: 0,
          position: 'absolute',
          right: 0,
          top: 0,
        }),
        barHc: css({
          backgroundColor: 'transparent',
          border: `1px solid ${theme.color.foreground.default}`,
        }),

        sharp: css({
          borderRadius: theme.layout.size0,
        }),
      },

      label: {
        base: css({
          ...theme.typography.labelRegular,
          color: theme.color.foreground.default,
          wordBreak: 'break-word',
        }),
        // if showPercentage is true, the label will be following the bar
        floating: css({
          display: 'inline-flex',
          marginTop: theme.orbitalTwo,
          transition: 'margin-left 0.2s linear',
          marginLeft: `calc(${roundedValue}% ${floatingLabelOffset})`,
        }),
        circular: css({
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: theme.color.foreground.default,
          pointerEvents: 'none',
          textAlign: 'center',
        }),
      },

      circle: {
        base: css({
          strokeDasharray: Math.PI * 14,
        }),

        determinate: css({
          strokeDasharray: circumference,
          strokeDashoffset: dashoffset,
        }),
      },
    }),
    [
      circumference,
      color,
      dashoffset,
      floatingLabelOffset,
      roundedValue,
      size,
      theme,
      trackThickness,
      value,
    ]
  );
};

export default useStyles;
